import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Store reference to auth token getter (set by ClerkTokenProvider)
let _getAuthToken = null;
export const setApiSliceAuthGetter = (getter) => { _getAuthToken = getter; };

// Base query with error handling
const baseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  timeout: 30000,
  prepareHeaders: async (headers) => {
    headers.set('Content-Type', 'application/json');
    if (_getAuthToken) {
      try {
        const token = await _getAuthToken();
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }
      } catch (e) {
        // Token retrieval failed, proceed without it
      }
    }
    return headers;
  },
});

// Enhanced base query with error handling
const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error) {
    // Handle HTML responses (e.g., from auth failures or nginx errors)
    if (result.error.status === 'PARSING_ERROR' ||
        (typeof result.error.data === 'string' && result.error.data.includes('<!doctype'))) {
      console.warn('Received HTML instead of JSON - likely an auth or routing issue');
      return {
        error: {
          status: result.error.originalStatus || 500,
          data: { message: 'Server returned an unexpected response. Please try again.' },
        },
      };
    }

    console.error('API Error:', result.error);

    // Handle network switching related errors
    if (result.error.status === 500 && result.error.data?.message?.includes('network')) {
      console.warn('Network-related error detected, retrying in 1 second...');
      // Retry after a brief delay for network switching issues
      await new Promise(resolve => setTimeout(resolve, 1000));
      result = await baseQuery(args, api, extraOptions);
    }
  }

  return result;
};

// Create the API slice
export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Contract', 'Transaction', 'Balance', 'Health', 'Dashboard'],
  endpoints: (builder) => ({
    // Health check endpoint
    healthCheck: builder.query({
      query: () => '/health',
      providesTags: ['Health'],
    }),

    // Contract endpoints
    deployContract: builder.mutation({
      query: (contractData) => ({
        url: '/contracts/deploy',
        method: 'POST',
        body: contractData,
      }),
      invalidatesTags: ['Contract'],
    }),

    getUserContracts: builder.query({
      query: (walletAddress) => `/contracts/user/${walletAddress}`,
      providesTags: (result, error, walletAddress) => [
        { type: 'Contract', id: `USER_${walletAddress}` },
      ],
      // Force fresh data on network switches
      keepUnusedDataFor: 0,
    }),

    getAllContracts: builder.query({
      query: (params = {}) => ({
        url: '/contracts',
        params,
      }),
      providesTags: ['Contract'],
    }),

    getContractDetails: builder.query({
      query: (contractAddress) => `/contracts/${contractAddress}`,
      providesTags: (result, error, contractAddress) => [
        { type: 'Contract', id: contractAddress },
      ],
    }),

    verifyContract: builder.mutation({
      query: (contractAddress) => ({
        url: `/contracts/${contractAddress}/verify`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, contractAddress) => [
        { type: 'Contract', id: contractAddress },
      ],
    }),

    // Transaction endpoints
    transferTokens: builder.mutation({
      query: (transferData) => ({
        url: '/transactions/transfer',
        method: 'POST',
        body: transferData,
      }),
      invalidatesTags: ['Transaction', 'Balance'],
    }),

    mintTokens: builder.mutation({
      query: (mintData) => ({
        url: '/transactions/mint',
        method: 'POST',
        body: mintData,
      }),
      invalidatesTags: ['Transaction', 'Balance'],
    }),

    getBalance: builder.query({
      query: ({ contractAddress, walletAddress, chain }) => ({
        url: `/transactions/balance/${contractAddress}/${walletAddress}`,
        params: { chain },
      }),
      providesTags: (result, error, { contractAddress, walletAddress }) => [
        { type: 'Balance', id: `${contractAddress}_${walletAddress}` },
      ],
    }),

    getTransactionHistory: builder.query({
      query: ({ walletAddress, ...params }) => ({
        url: `/transactions/history/${walletAddress}`,
        params,
      }),
      providesTags: (result, error, { walletAddress }) => [
        { type: 'Transaction', id: `HISTORY_${walletAddress}` },
      ],
    }),

    getTransactionDetails: builder.query({
      query: (transactionHash) => `/transactions/${transactionHash}`,
      providesTags: (result, error, transactionHash) => [
        { type: 'Transaction', id: transactionHash },
      ],
    }),

    // Dashboard endpoints
    getDashboardStats: builder.query({
      query: () => '/dashboard/stats',
      providesTags: ['Dashboard'],
    }),

    getDashboardTransactions: builder.query({
      query: (params = {}) => ({
        url: '/dashboard/transactions',
        params,
      }),
      providesTags: ['Dashboard', 'Transaction'],
    }),

  }),
});

// Export hooks for usage in components
export const {
  // Health
  useHealthCheckQuery,

  // Contracts
  useDeployContractMutation,
  useGetUserContractsQuery,
  useGetAllContractsQuery,
  useGetContractDetailsQuery,
  useVerifyContractMutation,

  // Transactions
  useTransferTokensMutation,
  useMintTokensMutation,
  useGetBalanceQuery,
  useGetTransactionHistoryQuery,
  useGetTransactionDetailsQuery,

  // Dashboard
  useGetDashboardStatsQuery,
  useGetDashboardTransactionsQuery,

  // Util
  util: { invalidateTags },
} = apiSlice;

// Export API reducer
export default apiSlice.reducer;